/**
 * useSitePatrols Hook
 *
 * React Query hook for fetching patrol history for a specific site/address.
 * Used by the SiteInfoModal to display site details, today's patrols, and history.
 *
 * Features:
 * - Infinite query for paginated history
 * - Today's patrols in first page
 * - Site stats (30-day totals)
 * - 30-second stale time for caching
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import {
  fetchSitePatrols,
  SitePatrolsResponse,
  SitePatrolHistoryItem,
  SiteStats,
} from '../../../../../api/dispatch';

/** Query key for site patrols */
export const SITE_PATROLS_QUERY_KEY = ['sitePatrols'];

/** Stale time (30 seconds) */
const STALE_TIME = 30 * 1000;

/** Page size for history pagination */
const PAGE_SIZE = 20;

export interface UseSitePatrolsOptions {
  /** The address/site ID to fetch patrols for */
  addressId: number | null;
  /** Whether to enable fetching (default: true) */
  enabled?: boolean;
}

export interface UseSitePatrolsResult {
  /** Site address ID */
  addressId: number | null;
  /** Site name */
  siteName: string | null;
  /** Customer name */
  customerName: string | null;
  /** Full address string */
  fullAddress: string | null;
  /** Site latitude */
  latitude: number | null;
  /** Site longitude */
  longitude: number | null;
  /** Site statistics (30-day totals) */
  stats: SiteStats | null;
  /** Today's patrols at this site */
  todaysPatrols: SitePatrolHistoryItem[];
  /** Historical patrols (paginated) */
  history: SitePatrolHistoryItem[];
  /** Whether there are more history items */
  hasMore: boolean;
  /** Whether initial load is in progress */
  isLoading: boolean;
  /** Whether any fetch is in progress */
  isFetching: boolean;
  /** Whether the next page is being fetched */
  isFetchingNextPage: boolean;
  /** Whether an error occurred */
  isError: boolean;
  /** Error object if present */
  error: Error | null;
  /** Fetch the next page of history */
  fetchNextPage: () => void;
  /** Manually trigger a refresh */
  refetch: () => void;
}

/**
 * Hook to fetch patrol history for a specific site
 *
 * @param options - Configuration options
 * @returns Site info, patrols, and history with loading/error states
 */
export function useSitePatrols(options: UseSitePatrolsOptions): UseSitePatrolsResult {
  const { addressId, enabled = true } = options;
  const shouldFetch = enabled && addressId !== null;

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery<SitePatrolsResponse>({
    queryKey: [...SITE_PATROLS_QUERY_KEY, addressId],
    queryFn: async ({ pageParam = 0 }) => {
      if (!addressId) {
        throw new Error('addressId is required');
      }
      try {
        return await fetchSitePatrols(addressId, {
          limit: PAGE_SIZE,
          offset: pageParam as number,
        });
      } catch (err) {
        console.error('[useSitePatrols] Failed to fetch:', err);
        throw err;
      }
    },
    enabled: shouldFetch,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.hasMore) return undefined;
      return allPages.length * PAGE_SIZE;
    },
    initialPageParam: 0,
  });

  // Get first page for site info and today's patrols (with safe access)
  const firstPage = data?.pages?.[0];

  // Merge all history pages into a single array (with safe access)
  const allHistory = data?.pages?.flatMap((page) => page?.history ?? []) ?? [];

  // Check if there are more pages (with safe access)
  const hasMore = data?.pages?.[data.pages.length - 1]?.hasMore ?? false;

  return {
    addressId: firstPage?.addressId ?? null,
    siteName: firstPage?.siteName ?? null,
    customerName: firstPage?.customerName ?? null,
    fullAddress: firstPage?.fullAddress ?? null,
    latitude: firstPage?.latitude ?? null,
    longitude: firstPage?.longitude ?? null,
    stats: firstPage?.stats ?? null,
    todaysPatrols: firstPage?.todaysPatrols ?? [],
    history: allHistory,
    hasMore,
    isLoading: shouldFetch && isLoading,
    isFetching,
    isFetchingNextPage,
    isError,
    error: error instanceof Error ? error : null,
    fetchNextPage,
    refetch,
  };
}

export default useSitePatrols;
